import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import Select from "react-select";
import { getAssignedUsers, assignUsers, unassignUser, getUsers } from "../../apiCalls/caller";
import { toast } from "react-toastify";

const ProductsUsersModal = ({ show, onCloseClick, productId, onSaveSuccess, userType = 0 }) => {
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (show && productId) {
      loadData();
    }
  }, [show, productId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load assigned users
      const assignedResponse = await getAssignedUsers({ pro_id: productId });
      if (assignedResponse?.success) {
        const assigned = assignedResponse.data || [];
        setAssignedUsers(assigned);
        setSelectedUsers(assigned.map(u => ({ 
          value: u.user_id, 
          label: `${u.user_name} (${u.user_email})` 
        })));
      }

      // Load initial user list
      const usersResponse = await getUsers({ type: userType });
      if (usersResponse?.success) {
        const users = usersResponse.data || [];
        setAllUsers(users.map(u => ({ 
          value: u.user_id, 
          label: `${u.user_name} (${u.user_email})` 
        })));
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Error loading data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const userIds = selectedUsers.map(u => u.value);
      const response = await assignUsers(productId, userIds);
      
      if (response?.success) {
        toast.success(response.message || "Users assigned successfully");
        if (onSaveSuccess) onSaveSuccess();
        onCloseClick();
      } else {
        toast.error(response?.message || "Error saving");
      }
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Error saving changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMakeVisibleToAll = async () => {
    setIsSaving(true);
    try {
      const response = await assignUsers(productId, []);
      
      if (response?.success) {
        toast.success("Product visible to all users");
        setSelectedUsers([]);
        if (onSaveSuccess) onSaveSuccess();
        onCloseClick();
      } else {
        toast.error(response?.message || "Error updating");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error updating visibility");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUserInputChange = (inputValue) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setIsLoadingUsers(true);
      try {
        const params = inputValue
          ? { searchText: inputValue, type: userType }
          : { type: userType };
        const usersResponse = await getUsers(params);
        if (usersResponse?.success) {
          const users = usersResponse.data || [];
          setAllUsers(users.map(u => ({
            value: u.user_id,
            label: `${u.user_name} (${u.user_email})`
          })));
        }
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    }, 300);
  };

  const handleRemoveUser = async (userId) => {
    try {
      const response = await unassignUser({ pro_id: productId, user_id: userId });
      
      if (response?.success) {
        toast.success("User removed");
        setSelectedUsers(prev => prev.filter(u => u.value !== userId));
        setAssignedUsers(prev => prev.filter(u => u.user_id !== userId));
      } else {
        toast.error("Error removing user");
      }
    } catch (error) {
      console.error("Error removing user:", error);
      toast.error("Error removing user");
    }
  };

  return (
    <Modal isOpen={show} toggle={onCloseClick} centered={true} size="lg">
      <ModalHeader toggle={onCloseClick}>
        Product Users Management
      </ModalHeader>
      <ModalBody className="py-3 px-4">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <>
            <div className="mt-2">
              <div className="alert alert-info">
                <i className="ri-information-line me-2"></i>
                {assignedUsers.length === 0 ? (
                  <span>
                    <strong>Visible to all members.</strong> This product is visible to all member users.
                  </span>
                ) : (
                  <span>
                    <strong>Restricted to {assignedUsers.length} user(s).</strong> Only selected users can see this product.
                  </span>
                )}
              </div>

              {assignedUsers.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">Users with current access:</h6>
                  <div className="list-group">
                    {assignedUsers.map(user => (
                      <div key={user.user_id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{user.user_name}</strong>
                          <br />
                          <small className="text-muted">{user.user_email}</small>
                        </div>
                        <button 
                          onClick={() => handleRemoveUser(user.user_id)}
                          className="btn btn-sm btn-danger"
                          disabled={isSaving}
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">
                  <strong>Select Users</strong>
                </label>
                <Select
                  isMulti
                  value={selectedUsers}
                  onChange={(selected) => setSelectedUsers(selected || [])}
                  options={allUsers}
                  onInputChange={handleUserInputChange}
                  placeholder="Select users..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isDisabled={isSaving}
                  isLoading={isLoadingUsers}
                  noOptionsMessage={() => "No users available"}
                />
                <small className="text-muted">
                  Leave empty to make the product visible to all members
                </small>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-light"
                onClick={onCloseClick}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleMakeVisibleToAll}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-eye-line me-1"></i>
                    Visible to All
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

ProductsUsersModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  productId: PropTypes.number,
  onSaveSuccess: PropTypes.func,
  userType: PropTypes.number,
};

export default ProductsUsersModal;
